'use client';
import React, { useRef, useState } from 'react';
import { runningClientSide } from '@benzinga/utils';
import WidgetStyles from './widget.module.scss';
import { ReactComponent as WnstnLogo } from './assets/wnstnLogo.svg';
import { ReactComponent as BenzingaAILogo } from './assets/benzinga-ai.svg';
import classNames from 'classnames';
import { WidgetModal } from './widgetModal';
import { useUser } from '@benzinga/user-context';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useRouter } from 'next/navigation';
import { WidgetType } from './widgetType';

export { WidgetType } from './widgetType';

export const LoadWidget = () => {
  if (!runningClientSide()) {
    return;
  }

  (function (d, s, n) {
    const container = document?.querySelector('.my-chat-container-container__widget');

    if (container) {
      const js = d.createElement(s) as HTMLScriptElement;
      js.id = n;
      js.src = 'https://widget.wnstn.ai/wnstnai/createWidget.js';
      js.defer = true;
      container.appendChild(js);
    }
  })(document, 'script', 'wnstn-widget');
};

interface Props {
  questions?: string[];
  articleID?: number;
  userID?: number;
  ticker?: string;
  isCrypto?: boolean;
  title?: string;
  small?: boolean;
  widgetType?: WidgetType;
}

export const DEFAULT_QUESTIONS = [
  'What design flaws delay Nvidia chips?',
  'How does this affect Meta AI plans?',
  'Which competitors may benefit?',
  'What the new release timeline?',
  'Is Microsoft seeking alternatives?',
  'Explain the impact on Nvidia stock',
];

const PARAM_KEY = 'wnstn_question';

export const WNSTNWidget: React.FC<Props> = ({
  articleID,
  questions = DEFAULT_QUESTIONS,
  small = false,
  title = 'Got Questions? Ask',
  widgetType = WidgetType.FollowUp,
}) => {
  const session = React.useContext(SessionContext);
  const user = useUser();
  const [selectedQuestion, setSelectedQuestion] = React.useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const impressed = useRef<boolean>(false);
  const router = useRouter();

  const getWidgetParams = React.useCallback(
    (question: string) => {
      const accessType = user?.accessType === 'subscribed' || user?.accessType === 'trialing' ? 'pro' : 'www';

      const messageObj = {
        follow_up_question: sanitizeHTML(question ?? ''),
        proxy: `https://wnstn-api.benzinga.com/${accessType}`,
        userId: String(user?.id),
      };

      if (articleID) {
        messageObj['article_index'] = articleID;
      }

      return messageObj;
    },
    [user, articleID],
  );

  const trackQuestionSelect = React.useCallback(
    (question: string) => {
      const eventName = widgetType === WidgetType.Asset ? 'asset_wnstn_widget_click' : 'follow_up_wnstn_widget_click';

      session.getManager(TrackingManager).trackWnstnEvent(eventName, {
        node_id: articleID ? String(articleID) : undefined,
        page_link: window.location.href,
        question: question,
      });
    },
    [session, widgetType, articleID],
  );

  React.useEffect(() => {
    if (runningClientSide()) {
      if (window.location.search) {
        const urlParams = new URLSearchParams(window.location.search);
        const question = urlParams.get(PARAM_KEY);

        if (question) {
          setSelectedQuestion(question);
          trackQuestionSelect(question);
        }
      }
    }
  }, [user, setSelectedQuestion, session, articleID, trackQuestionSelect]);

  React.useEffect(() => {
    window['chatInitParams'] = getWidgetParams(selectedQuestion ?? '');

    if (!impressed.current) {
      impressed.current = true;
      const eventName = widgetType === WidgetType.Asset ? 'asset_wnstn_widget_view' : 'follow_up_wnstn_widget_view';
      session.getManager(TrackingManager).trackWnstnEvent(eventName, {
        node_id: articleID ? String(articleID) : undefined,
        page_link: window.location.href,
      });
    }

    if (loaded || !selectedQuestion) {
      return;
    }

    setLoaded(true);

    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      LoadWidget();
    } else {
      window.addEventListener(
        'load',
        () => {
          LoadWidget();
        },
        { once: true },
      );
    }
  }, [loaded, selectedQuestion, articleID, user, getWidgetParams, widgetType, session]);

  const onQuestionSelect = (question: string) => {
    if (!user?.id) {
      let currentUrl = document.location.href;
      const prefix = currentUrl.includes('?') ? '&' : '?';
      currentUrl += `${prefix}${PARAM_KEY}=${sanitizeHTML(question)}`;
      router.push(`/login?next=${currentUrl}`);
    } else {
      const modal = document.getElementById('wnstn-widget-modal');
      if (modal) {
        modal.style.display = 'flex';
      }

      if (loaded) {
        const iframe = document.querySelector('.my-chat-container iframe') as HTMLIFrameElement;
        if (iframe) {
          const messageObj = getWidgetParams(question);
          iframe.src = `https://reactapp.wnstn.ai/?widget_params=${JSON.stringify(messageObj)}`;
        }
      }

      trackQuestionSelect(question);
      setSelectedQuestion(question);
    }
  };

  const onModalClose = React.useCallback(() => {
    setSelectedQuestion(null);
  }, []);

  return (
    <>
      <WidgetModal onClose={onModalClose} />
      <div className={small ? WidgetStyles.WidgetSmall : ''}>
        <div className={WidgetStyles.WidgetWrapper}>
          <div className={WidgetStyles.WidgetHeader}>
            {title && <div className={WidgetStyles.WidgetHeaderTitle}>{title}</div>}
            <BenzingaAILogo />
          </div>
          <div className={WidgetStyles.WidgetQuestions}>
            {questions?.map(question => (
              <div
                className={classNames(WidgetStyles.WidgetQuestion, 'mr-2 mb-2')}
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(question) }}
                key={question}
                onClick={() => onQuestionSelect(question)}
              />
            ))}
          </div>
          <div className={WidgetStyles.Footer}>
            <span className={WidgetStyles.WidgetFooterText}>Powered By</span>
            <a href="https://wnstn.ai/" rel="noreferrer" target="_blank">
              <WnstnLogo className="ml-2" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default WNSTNWidget;
